import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/toast";
import { Button, Loading } from "@/components/ui";
import { SelectDialog } from "@/components/select-dialog";
import { StepPersonal, PERSONAL_KEYS } from "@/components/wizard/step-personal";
import { StepMission } from "@/components/wizard/step-mission";
import { StepPreferences, emptyPrefs } from "@/components/wizard/step-preferences";
import { StepPhotos } from "@/components/wizard/step-photos";

const STEP_NAMES = ["Personal info", "Your mission", "Preferences", "Photos"];

function validate(step, personal, mission, prefs, user) {
  const e = {};
  if (step === 0) {
    if (!personal.gender) e.gender = "Select your gender";
    if (!personal.occupation?.trim()) e.occupation = "Enter your occupation";
    if (!personal.marital_status) e.marital_status = "Select your marital status";
    if (!personal.preferred_language) e.preferred_language = "Select a language";
    if (!personal.bio?.trim() || personal.bio.trim().length < 10) e.bio = "Write a short bio (10+ characters)";
  }
  if (step === 1 && !mission) e.mission = "Choose one mission to continue";
  if (step === 2 && (prefs.min_age ?? 18) > (prefs.max_age ?? 100)) e.age = "Minimum age cannot exceed maximum age";
  if (step === 3 && user.photos.length === 0) e.photos = "Add at least one photo";
  return e;
}

export default function Onboarding() {
  const { user, status } = useAuth();
  const { data: options } = useQuery({ queryKey: ["options"], queryFn: () => api("/options", { auth: false }) });
  if (status === "guest") return <Navigate to="/landing" replace />;
  if (!user || !options) return <Loading />;
  return <Wizard user={user} options={options} />;
}

function Wizard({ user, options }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const editMode = params.get("mode") === "edit";
  const initialStep = params.get("step") ? Number(params.get("step")) : Math.min(Math.max((user.onboarding_step ?? 2) - 2, 0), 3);
  const [step, setStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectKey, setSelectKey] = useState(null);
  const [personal, setPersonal] = useState(() => Object.fromEntries(PERSONAL_KEYS.map((k) => [k, user.profile?.[k] ?? null])));
  const [mission, setMission] = useState(user.mission ?? null);
  const [prefs, setPrefs] = useState(() => ({ ...emptyPrefs, ...(user.preferences ?? {}) }));
  const [interests, setInterests] = useState(user.profile?.interests ?? []);

  const next = async () => {
    const e = validate(step, personal, mission, prefs, user);
    setErrors(e);
    const first = Object.values(e)[0];
    if (first) return toast(first, "error");
    setSaving(true);
    try {
      const body = { onboarding_step: Math.min(Math.max(user.onboarding_step, step + 3), 6) };
      if (step === 0) body.profile = personal;
      if (step === 1) body.mission = mission;
      if (step === 2) { body.preferences = prefs; body.profile = { interests }; }
      const u = await api("/profile/me", { method: "PATCH", body });
      setUser(u);
      if (editMode) { toast("Profile updated", "success"); navigate(-1); }
      else if (step < 3) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
      else if (u.onboarding_complete) { toast("Your profile is ready!", "success"); navigate("/discover", { replace: true }); }
      else { toast("Please complete all required steps", "error"); setStep(0); }
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const back = () => (editMode || step === 0 ? navigate(-1) : setStep(step - 1));
  const sel = selectKey ? {
    nationality: { title: "Nationality", options: options.countries, multi: false, value: personal.nationality ? [personal.nationality] : [] },
    preferred_language: { title: "Preferred language", options: options.languages, multi: false, value: personal.preferred_language ? [personal.preferred_language] : [] },
    countries: { title: "Preferred nationalities", options: options.countries, multi: true, value: prefs.countries },
    residence_countries: { title: "Preferred countries of residence", options: options.countries, multi: true, value: prefs.residence_countries },
  }[selectKey] : null;
  const onSelect = (v) => {
    if (selectKey === "nationality" || selectKey === "preferred_language") setPersonal({ ...personal, [selectKey]: v[0] ?? null });
    else setPrefs({ ...prefs, [selectKey]: v });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-2">
          <button data-testid="wizard-back-button" aria-label="Back" onClick={back} disabled={!editMode && step === 0} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-[#F4EBE6] disabled:invisible">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <span className="text-[13px] font-bold text-muted-foreground" data-testid="wizard-progress-text">
            {editMode ? `Edit · ${STEP_NAMES[step]}` : `Step ${step + 2} of 5 · ${STEP_NAMES[step]}`}
          </span>
          <span className="w-11" />
        </div>
        {!editMode ? (
          <div className="mx-auto flex max-w-3xl gap-1.5 px-8 pb-3" data-testid="wizard-progress-bar">
            {[0, 1, 2, 3, 4].map((i) => <span key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step + 1 ? "bg-primary" : "bg-border"}`} />)}
          </div>
        ) : null}
      </header>
      <main key={step} className="fade-up mx-auto w-full max-w-3xl flex-1 px-6 py-8">
        {step === 0 ? <StepPersonal value={personal} onChange={setPersonal} options={options} age={user.age} errors={errors} openSelect={setSelectKey} />
          : step === 1 ? <StepMission value={mission} onChange={setMission} error={errors.mission} />
          : step === 2 ? <StepPreferences value={prefs} onChange={setPrefs} interests={interests} onInterests={setInterests} options={options} error={errors.age} openSelect={setSelectKey} />
          : <StepPhotos me={user} onUpdated={setUser} />}
        {errors.photos && step === 3 ? <p className="mt-3 text-sm font-medium text-[#9E3C3E]">{errors.photos}</p> : null}
      </main>
      <footer className="sticky bottom-0 border-t border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl justify-end px-6 py-3">
          <Button testId="wizard-next-button" title={editMode ? "Save" : step === 3 ? "Finish & discover matches" : "Continue"} loading={saving} onClick={next} className="w-full sm:w-auto sm:min-w-[220px]" />
        </div>
      </footer>
      {sel ? <SelectDialog testId={`wizard-select-${selectKey}`} open onClose={() => setSelectKey(null)} title={sel.title} options={sel.options} value={sel.value} multi={sel.multi} onChange={onSelect} /> : null}
    </div>
  );
}
